import React, { FC, useState, useEffect, useCallback, ReactNode } from 'react'
import { appInsights } from '../api/AppInsights'
import {
  useToast,
  Container,
  Grid,
  Text,
  SearchBox,
  Dropdown,
  Table,
  TableRow,
  TableCell,
  Button,
  Loader,
  Modal,
  useModal,
  FormInput,
  TabContent,
  TabMenu,
  DateInput,
  IDateInputDates,
} from '@aurecon-creative-technologies/styleguide'
import Page from '../components/Page'
import {
  propertyDataMap,
  PropertyData,
  isPropertyData,
  RawPropertyData,
  wrapPropertyData,
  mapKeysToRawPropertyData,
} from '../models/data/PropertyData'
import { getDBInfo, getDBMeta } from '../api/DbInfoService'
import { submitPropEditForm } from '../api/FormSubmitService'

import Style from '../styles/PropertyPage.module.sass'
import { exportToCSV } from '../helpers/exporter'

interface ITableHeader {
  // column header for data table
  label: string // column label
  key: string // key to retrieve property data
  filter?: ReactNode // component to be used as filter handle
}

const Property: FC = () => {
  const { addToast } = useToast()
  if (appInsights) appInsights.trackPageView({ name: 'Property' })
  // -------- state hooks
  const [propData, setPropData] = useState<PropertyData[]>() // full property data list
  const [selectedProp, setSelectedProp] = useState<PropertyData>() // data of selected property
  const [editedProp, setEditProp] = useState<Partial<PropertyData>>() // edited property data
  const [formPayload, setFormPayload] = useState<Partial<RawPropertyData>>() // payload to be submitted
  const [propFilter, setPropFilter] = useState<Partial<PropertyData>>({}) // filter on property data
  // ui elements
  const [tableLoading, setTableLoading] = useState<boolean>(false)
  const [modalLoading, setModalLoading] = useState<boolean>(false)
  const [editForm, setEditForm] = useState<boolean>(false)

  type DropdownOption = {
    insptAccessStatus: string[]
    insptFieldStatus: string[]
    rectStage: string[]
    rectStatus: string[]
    insptCustomerResponse: string[]
    projectName: string[]
  }
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption>()

  const { isShowing, toggleModal } = useModal()

  const exportdata = () => {
    if (propData) {
      const filename = `Properties.${new Date().toISOString()}.csv`
      addToast({
        type: 'info',
        message: `Export To CSV [${filename}]`,
        timeout: 3000,
      })
      exportToCSV(propData, filename)
    }
  }

  const fetchPropDataWithFilter = useCallback(async (filter: Partial<PropertyData>) => {
    setTableLoading(true)
    // load property data into memory for display
    const result = await getDBInfo({ filters: JSON.stringify(mapKeysToRawPropertyData(filter)) })
    const resultmeta = await getDBMeta({})
    console.log('RES', result, resultmeta)
    if (result?.success && resultmeta?.success && !!result.data && !!resultmeta.data) {
      // parse data from response
      const rawData = result.data['response']['data']
      const rawDropdownOpt = result.data['response']['filters'] as DropdownOption

      // map raw data
      const data = []
      for (const raw of rawData) {
        const wrappedData = wrapPropertyData(raw, propertyDataMap)
        if (wrappedData && isPropertyData(wrappedData)) {
          data.push(wrappedData)
        }
      }
      setPropData(data)

      // map dropdown options
      let wrappedDropdownOpt = {} as DropdownOption
      for (const mapKey in propertyDataMap) {
        const rawKey: keyof RawPropertyData = propertyDataMap[mapKey].rawKey
        const options = rawDropdownOpt[rawKey]
        wrappedDropdownOpt = { ...wrappedDropdownOpt, [mapKey]: options }
      }

      wrappedDropdownOpt['projectName'] = resultmeta.data['projectNames']

      setDropdownOptions(wrappedDropdownOpt)

      setTableLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPropDataWithFilter(propFilter)
  }, [fetchPropDataWithFilter, propFilter])

  // ----------- user action handlers
  const onCellClick = (propId: number) => () => {
    // action on the selected property
    const property = propData?.find((x) => x.propId === propId)
    if (property) {
      setSelectedProp(property)
      setEditProp({ ...property })
      setEditForm(false)
      setFormPayload({})
      toggleModal()
    }
  }

  const onSearchChange = (key: keyof PropertyData) => (value: string | number) => {
    // search box text change
    let newFilter: Partial<PropertyData> = { ...propFilter }
    if (value) {
      newFilter = { ...propFilter, [key]: value }
    } else {
      // remove property if filter is empty
      delete newFilter[key]
    }
    setPropFilter(newFilter)
  }

  const onDateChange = (value: IDateInputDates) => {
    let newFilter: Partial<PropertyData> = { ...propFilter }
    if (value.startDate && value.endDate) {
      newFilter = {
        ...propFilter,
        startDate: `${value.startDate?.getTime()}`,
        endDate: `${value.endDate?.getTime()}`,
      }
    } else {
      // remove property if filter is empty
      delete newFilter.startDate
      delete newFilter.endDate
    }
    setPropFilter(newFilter)
  }

  const onSearchClear = (key: keyof PropertyData) => () => {
    // search box text change
    const newFilter: Partial<PropertyData> = { ...propFilter }
    delete newFilter[key]
    setPropFilter(newFilter)
  }

  const onDropdownChange = (key: keyof PropertyData) => (selectedItemId: string | number) => {
    // new filter selected
    let newFilter: Partial<PropertyData> = { ...propFilter }
    if (selectedItemId) {
      newFilter = { ...propFilter, [key]: selectedItemId }
    } else {
      // remove property if filter is empty
      delete newFilter[key]
    }
    setPropFilter(newFilter)
  }

  const toggleFormEdit = () => {
    setEditForm(!editForm)
  }

  const onEditFormChange = (key: keyof PropertyData) => (value: string | number) => {
    const newData = { ...editedProp, [key]: value }
    const rawKey = propertyDataMap[key]?.rawKey
    const dataType = propertyDataMap[key]?.dataType
    if (rawKey) {
      // convert value to proper format
      let payloadValue = undefined
      switch (dataType) {
        case 'date':
          payloadValue = Date.parse(value.toString())
          break
        default:
          payloadValue = value
      }
      // store payload
      let newFormPayload = { ...formPayload, 'Property Number': selectedProp?.propId }
      newFormPayload = { ...newFormPayload, [rawKey]: payloadValue }
      setFormPayload(newFormPayload)
    }
    setEditProp(newData)
  }

  const onCancelFormEdit = () => {
    // discard user input
    setEditProp({ ...selectedProp })
    setEditForm(false)
    setFormPayload({})
  }

  const onSubmitFormEdit = async () => {
    // do nothing if payload is empty
    if (formPayload && Object.keys(formPayload).length === 0) return
    // submit form data
    setEditForm(false)
    setModalLoading(true)
    if (formPayload) {
      const result = await submitPropEditForm({ propData: formPayload })
      // TODO: add error message for failed submission
      console.log('form submitted')
      console.log(result)
    }
    setModalLoading(false)
    fetchPropDataWithFilter(propFilter)
    toggleModal()
  }

  // ------------- React Components
  const loadingSpinner = (content: string) => {
    return (
      <Grid cssClass={Style.loader}>
        <Loader label={content} />
      </Grid>
    )
  }
  // ---- table setup
  type TableHeader = {
    label: string
    key: keyof PropertyData
    filter?: React.ReactElement
  }
  // table header
  const tableHeaders: TableHeader[] = [
    {
      label: 'Property ID',
      key: 'propId',
      filter: <SearchBox placeholder='Search' onSearch={onSearchChange('propId')} onClear={onSearchClear('propId')} />,
    },
    {
      label: 'Property Address',
      key: 'propAdd',
      filter: (
        <SearchBox placeholder='Search' onSearch={onSearchChange('propAdd')} onClear={onSearchClear('propAdd')} />
      ),
    },
    {
      label: 'Polygon Name',
      key: 'polygonName',
      filter: (
        <SearchBox
          placeholder='Search'
          onSearch={onSearchChange('polygonName')}
          onClear={onSearchClear('polygonName')}
        />
      ),
    },

    {
      label: 'Project Name',
      key: 'projectName',
      filter: (
        <Dropdown
          placeholder='-'
          default
          selectedItem={propFilter['projectName']}
          items={dropdownOptions ? dropdownOptions['projectName'].map((opt) => ({ id: opt, label: opt })) : null}
          clearSelectionItem='- Clear -'
          onSelectItem={onDropdownChange('projectName')}
        />
      ),
    },
    // {
    //   label: 'Project Name',
    //   key: 'projectName',
    //   filter: (
    //     <SearchBox
    //       placeholder='Search'
    //       onSearch={onSearchChange('projectName')}
    //       onClear={onSearchClear('projectName')}
    //     />
    //   ),
    // },
    {
      label: 'Customer Response',
      key: 'insptCustomerResponse',
      filter: (
        <Dropdown
          placeholder='-'
          default
          selectedItem={propFilter['insptCustomerResponse']}
          items={
            dropdownOptions
              ? dropdownOptions['insptCustomerResponse'].map((opt: string) => ({ id: opt, label: opt }))
              : null
          }
          clearSelectionItem='- Clear -'
          onSelectItem={onDropdownChange('insptCustomerResponse')}
        />
      ),
    },

    {
      label: 'Response Details',
      key: 'insptCustomerResponseDetails',
      // filter: (
      //   <Dropdown
      //     placeholder='-'
      //     default
      //     selectedItem={propFilter['insptCustomerResponseDetails']}
      //     items={dropdownOptions ? dropdownOptions['insptCustomerResponseDetails'].map((opt:any) => ({ id: opt, label: opt })) : null}
      //     clearSelectionItem='- Clear -'
      //     onSelectItem={onDropdownChange('insptCustomerResponseDetails')}
      //   />
      // ),
    },

    {
      label: 'Inspection Access Status',
      key: 'insptAccessStatus',
      filter: (
        <Dropdown
          placeholder='-'
          default
          selectedItem={propFilter['insptAccessStatus']}
          items={dropdownOptions ? dropdownOptions['insptAccessStatus'].map((opt) => ({ id: opt, label: opt })) : null}
          clearSelectionItem='- Clear -'
          onSelectItem={onDropdownChange('insptAccessStatus')}
        />
      ),
    },
    {
      label: 'Inspection Field Status',
      key: 'insptFieldStatus',
      filter: (
        <Dropdown
          placeholder='-'
          default
          selectedItem={propFilter['insptFieldStatus']}
          items={dropdownOptions ? dropdownOptions['insptFieldStatus'].map((opt) => ({ id: opt, label: opt })) : null}
          clearSelectionItem='- Clear -'
          onSelectItem={onDropdownChange('insptFieldStatus')}
        />
      ),
    },
    // {
    //   label: 'Rectification Stage',
    //   key: 'rectStage',
    //   filter: (
    //     <Dropdown
    //       placeholder='-'
    //       default
    //       selectedItem={propFilter['rectStage']}
    //       items={dropdownOptions ? dropdownOptions['rectStage'].map((opt) => ({ id: opt, label: opt })) : null}
    //       clearSelectionItem='- Clear -'
    //       onSelectItem={onDropdownChange('rectStage')}
    //     />
    //   ),
    // },
    // {
    //   label: 'Retification Overall Status',
    //   key: 'rectStatus',
    //   filter: (
    //     <Dropdown
    //       placeholder='-'
    //       default
    //       selectedItem={propFilter['rectStatus']}
    //       items={dropdownOptions ? dropdownOptions['rectStatus'].map((opt) => ({ id: opt, label: opt })) : null}
    //       clearSelectionItem='- Clear -'
    //       onSelectItem={onDropdownChange('rectStatus')}
    //     />
    //   ),
    // },
    {
      label: 'Record Modified',
      key: 'modTimestamp',
      filter: (
        <DateInput
          default
          dates={{
            startDate: propFilter.startDate ? new Date(Number.parseInt(propFilter.startDate)) : null,
            endDate: propFilter.endDate ? new Date(Number.parseInt(propFilter.endDate)) : null,
          }}
          rangeType={true}
          onDateChange={onDateChange}
        />
      ),
    },
    { label: '', key: 'exportcsv', filter: <Button size='small' icon='download' onClick={exportdata} /> },
  ]

  // table content
  const tableContent = (headers: ITableHeader[], data?: PropertyData[]) => {
    // return empty row with null or invalid data
    // TODO: add error message
    if (!data) {
      return <TableRow></TableRow>
    }

    // data table content
    return data.map((row: PropertyData) => {
      const cells = headers.map((header: ITableHeader) => {
        if (!isPropertyData(row)) return null

        // table cell content
        const dataKey = header.key
        const dataValue = row[dataKey]
        return (
          <TableCell key={dataKey} onClick={onCellClick(row.propId)}>
            {dataValue}
          </TableCell>
        )
      })

      // populate table row
      return <TableRow key={row.propId}>{cells}</TableRow>
    })
  }

  const propDataTable = () => {
    // data table for property data
    return (
      <Grid xs={12}>
        <Table headers={tableHeaders}>{tableLoading ? null : tableContent(tableHeaders, propData)}</Table>
        {tableLoading ? loadingSpinner('Loading Property Data') : null}
      </Grid>
    )
  }

  const propDetailFormTabContent = (mapKeys: (keyof PropertyData)[]) => {
    // content of each tab of property detail form
    return (
      <Grid item xs={12}>
        {mapKeys.map((key) => {
          // extract field properties
          const label = propertyDataMap[key]?.label || propertyDataMap[key]?.rawKey
          // const dataType = propertyDataMap[key]?.dataType
          const options = propertyDataMap[key]?.options
          const readOnly = propertyDataMap[key]?.readOnly
          const hidden = propertyDataMap[key]?.hidden
          const group = propertyDataMap[key]?.group
          // if rectification stage is No, then disable edit in Rectification tab
          const hideByRectStage = group === 'rect' && editedProp?.rectStage === 'No'

          if (hidden) return // skip this field if it's hidden

          const displayvalue = editedProp ? editedProp[key]?.toString() : undefined

          return (
            <Grid key={key} cell item xs={12}>
              {options && editForm && !hideByRectStage ? (
                <Dropdown
                  label={label}
                  items={options.map((opt) => ({ id: opt, label: opt }))}
                  selectedItem={editedProp ? editedProp[key]?.toString() : undefined}
                  onSelectItem={onEditFormChange(key)}
                />
              ) : (
                <FormInput
                  label={label}
                  placeholder='---'
                  value={displayvalue}
                  readonly={!editForm || readOnly || hideByRectStage}
                  default
                  onChange={onEditFormChange(key)}
                  // type={dataType === 'date' ? 'date' : 'text'}
                  type={'text'}
                />
              )}
            </Grid>
          )
        })}
      </Grid>
    )
  }

  const propDetailForm = () => {
    // property detail form with fields groupped in tab menu
    // extract keys based on grouping
    const mapKeys = Object.keys(propertyDataMap) as (keyof PropertyData)[]
    const propKeys = [] as (keyof PropertyData)[]
    const insptKeys = [] as (keyof PropertyData)[]
    const rectKeys = [] as (keyof PropertyData)[]
    for (const key of mapKeys) {
      switch (propertyDataMap[key]?.group) {
        case 'prop':
          propKeys.push(key)
          break
        case 'inspt':
          insptKeys.push(key)
          break
        case 'rect':
          rectKeys.push(key)
          break
      }
    }
    return (
      <Grid row>
        <Grid item xs={11} cssClass={Style.topRow}>
          <Text type='h4'>{selectedProp?.propAdd}</Text>
          {editForm ? null : <Button label='Edit' onClick={toggleFormEdit} cssClass={Style.button} />}
        </Grid>
        <TabMenu
          tabs={[
            { id: 0, label: 'Property' },
            { id: 1, label: 'Inspection' },
            { id: 2, label: 'Rectification' },
          ]}
          type='solid'
        >
          <TabContent for={0}>{propDetailFormTabContent(propKeys)}</TabContent>
          <TabContent for={1}>{propDetailFormTabContent(insptKeys)}</TabContent>
          <TabContent for={2}>{propDetailFormTabContent(rectKeys)}</TabContent>
        </TabMenu>
      </Grid>
    )
  }

  //  ---------- Render
  return (
    <Page footer>
      <Container fluid cssClass={Style.pageWrapper}>
        <Grid cssClass={Style.body}>
          <Modal
            isShowing={isShowing}
            onClose={toggleModal}
            actions={
              editForm
                ? [
                    { label: 'Cancel', type: 'secondary', onClick: onCancelFormEdit },

                    { label: 'Submit', onClick: onSubmitFormEdit },
                  ]
                : undefined
            }
          >
            {modalLoading ? loadingSpinner('Submitting Data') : propDetailForm()}
          </Modal>
          {propDataTable()}
        </Grid>
      </Container>
    </Page>
  )
}

export default Property
